import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { X, FileText, Loader2, ChevronDown, Search } from "lucide-react";
import { UserData } from "../../types/user-data.type";
import useApi from "../../hooks/api/useApi";
import { Group } from "../../types/group.type";
import IranPlateInput from "./IranPlateInput";

interface ExistingFile {
  id: string;
  url: string;
  name: string;
  title:string;
  type: string;
}

interface NewFile{
  file:File,
  title:string;
}

interface MetaEntry {
  key: string;
  value: string;
}

interface FormState {
  title: string;
  code: string;
  description: string;
}

const BASE_URL = process.env.REACT_APP_BASE_URL;
const LAST_GROUP_KEY = "lastSelectedGroup";

const isImage = (type: string) => type.startsWith("image");

// ─── GroupSelector ────────────────────────────────────────────────────────────

interface GroupSelectorProps {
  value: Group | null;
  onChange: (group: Group | null) => void;
  authToken: string;
  error?: string;
}

function GroupSelector({ value, onChange, authToken, error }: GroupSelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { request } = useApi();

  const fetchGroups = useCallback(
    async (q: string) => {
      setLoading(true);
      try {
        await request(
          `/groups?search=${encodeURIComponent(q)}&page=1`,
          {
            method: "Get",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + authToken,
            },
          },
          (result) => setGroups(result.payload.items ?? [])
        );
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // debounce جستجو
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchGroups(query), 300);
    return () => clearTimeout(t);
  }, [query, open, fetchGroups]);

  // بستن با کلیک بیرون
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleOpen = () => {
    setOpen(true);
    setQuery("");
    fetchGroups("");
  };

  const handleSelect = (group: Group) => {
    onChange(group);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* trigger */}
      <button
        type="button"
        onClick={handleOpen}
        className={`w-full flex items-center justify-between border rounded px-3 py-2 bg-white text-sm ${error ? "border-red-500" : "border-gray-300"
          } hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500`}
      >
        <span className={value ? "text-gray-800" : "text-gray-400"}>
          {value ? value.name : "انتخاب گروه..."}
        </span>
        <div className="flex items-center gap-1">
          {value && (
            <span
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 cursor-pointer p-0.5"
            >
              <X size={14} />
            </span>
          )}
          <ChevronDown size={16} className="text-gray-400" />
        </div>
      </button>

      {/* dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
          {/* search */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="جستجو..."
                className="w-full pr-8 pl-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* list */}
          <ul className="max-h-48 overflow-y-auto">
            {loading ? (
              <li className="flex justify-center py-4">
                <Loader2 size={16} className="animate-spin text-gray-400" />
              </li>
            ) : groups.length === 0 ? (
              <li className="py-4 text-center text-sm text-gray-400">گروهی یافت نشد</li>
            ) : (
              groups.map((g) => (
                <li
                  key={g.id}
                  onClick={() => handleSelect(g)}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 ${value?.id === g.id ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-700"
                    }`}
                >
                  <div>{g.name}</div>
                  {g.description && (
                    <div className="text-xs text-gray-400 truncate">{g.description}</div>
                  )}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── InsuranceForm ────────────────────────────────────────────────────────────

export default function InsuranceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState<FormState>({ title: "", code: "", description: "" });
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [meta, setMeta] = useState<MetaEntry[]>([{ key: "", value: "" }]);
  const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]);
  const [removedFileIds, setRemovedFileIds] = useState<string[]>([]);
  const [newFiles, setNewFiles] = useState<NewFile[]>([]);
  const [newFileTitle, setNewFileTitle] = useState<string>('');
  const [errors, setErrors] = useState<Partial<FormState & { files: string; groupId: string }>>({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);

  const user = localStorage.getItem("user");
  const userData: UserData = JSON.parse(user ?? "");
  const { request } = useApi();

  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + userData.authToken,
  };

  // ── بارگذاری اولیه ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit) {
      // حالت ایجاد: گروه آخر از localStorage
      const saved = localStorage.getItem(LAST_GROUP_KEY);
      if (saved) {
        try {
          setSelectedGroup(JSON.parse(saved));
        } catch {
          localStorage.removeItem(LAST_GROUP_KEY);
        }
      }
      return;
    }

    // حالت ویرایش: دریافت داده از API
    request(
      `/insurances/${id}`,
      { method: "Get", headers: authHeaders },
      (result) => {
        const ins = result.payload;
        setForm({ title: ins.title, code: ins.code, description: ins.description ?? "" });

        if (ins.metadata) {
          const entries = Object.entries(ins.metadata).map(([key, value]) => ({
            key,
            value: String(value),
          }));
          setMeta(entries.length ? entries : [{ key: "", value: "" }]);
        }

        if (ins.files) setExistingFiles(ins.files);

        // گروه بیمه‌نامه (اگه API گروه رو برگردوند)
        if (ins.group) {
          setSelectedGroup(ins.group);
        } else if (ins.groupId) {
          // اگه فقط groupId داشت، از API گروه‌ها می‌گیریم
          request(
            `/groups/${ins.groupId}`,
            { method: "Get", headers: authHeaders },
            (r) => setSelectedGroup(r.payload)
          );
        }
      }
    ).finally(() => setInitialLoading(false));
  }, [id, isEdit]);

  // ── ذخیره گروه انتخاب‌شده در localStorage ──────────────────────────────────
  const handleGroupChange = (group: Group | null) => {
    setSelectedGroup(group);
    if (group) {
      localStorage.setItem(LAST_GROUP_KEY, JSON.stringify(group));
    } else {
      localStorage.removeItem(LAST_GROUP_KEY);
    }
  };

  // ── اعتبارسنجی ──────────────────────────────────────────────────────────────
  const validate = () => {
    const e: typeof errors = {};
    if (!form.title.trim()) e.title = "عنوان الزامی است";
    if (!form.code.trim()) e.code = "پلاک الزامی است";
    if (!selectedGroup) e.groupId = "انتخاب گروه الزامی است";
    if (existingFiles.length - removedFileIds.length + newFiles.length === 0)
      e.files = "حداقل یک فایل الزامی است";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── ارسال فرم ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    try {
      // آپلود فایل‌های جدید
      const uploadedIds: string[] = [];
      await Promise.all(
        newFiles.map(async (file) => {
          const fd = new FormData();
          fd.append("file", file.file);
          fd.append("title",file.title)
          await request(
            `/files`,
            {
              method: "Post",
              headers: { Authorization: "Bearer " + userData.authToken },
              body: fd,
            },
            (result) => uploadedIds.push(result.payload.id)
          );
        })
      );

      // حذف فایل‌های برداشته‌شده
      await Promise.all(
        removedFileIds.map((fid) =>
          request(`/files/${fid}`, { method: "Delete", headers: authHeaders }, () => { })
        )
      );

      const metadata = Object.fromEntries(
        meta.filter((m) => m.key.trim()).map((m) => [m.key, m.value])
      );

      const keptIds = existingFiles
        .filter((f) => !removedFileIds.includes(f.id))
        .map((f) => f.id);

      const payload = {
        ...form,
        metadata,
        filesIds: [...keptIds, ...uploadedIds],
        groupId: selectedGroup!.id,
      };

      const params = {
        method: isEdit ? "Put" : "Post",
        headers: authHeaders,
        body: JSON.stringify(payload),
      };

      await request(
        isEdit ? `/insurances/${id}` : `/insurances`,
        params,
        () => { }
      );

      navigate("/admin/insurances");
    } catch {
      setErrors((prev) => ({ ...prev, files: "خطا در ارسال. دوباره تلاش کنید." }));
    } finally {
      setLoading(false);
    }
  };

  const removeExisting = (fid: string) => setRemovedFileIds((prev) => [...prev, fid]);
  const removeNew = (index: number) => setNewFiles((prev) => prev.filter((_, i) => i !== index));
  const updateMeta = (index: number, field: "key" | "value", val: string) =>
    setMeta((prev) => prev.map((m, i) => (i === index ? { ...m, [field]: val } : m)));
              console.log('newFiles',newFiles) 

  if (initialLoading)
    return (
      <div className="flex justify-center p-10">
        <Loader2 className="animate-spin" />
      </div>
    );

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6 space-y-6">
      <h1 className="text-xl font-semibold">{isEdit ? "اصلاح بیمه نامه" : "بیمه نامه جدید"}</h1>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium mb-1">عنوان *</label>
        <input
          className={`w-full border rounded px-3 py-2 ${errors.title ? "border-red-500" : "border-gray-300"}`}
          value={form.title}
          onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
        />
        {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
      </div>

      {/* Code */}
      <div>
        <label className="block text-sm font-medium mb-1">پلاک *</label>
        <IranPlateInput
          valueText={form.code} onChange={(pla) => {
            setForm((p) => ({ ...p, code:pla }))
          }}
        />
        {errors.code && <p className="text-red-500 text-xs mt-1">{errors.code}</p>}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium mb-1">توضیحات</label>
        <textarea
          className="w-full border border-gray-300 rounded px-3 py-2"
          rows={3}
          value={form.description}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
        />
      </div>

      {/* Group */}
      <div>
        <label className="block text-sm font-medium mb-1">گروه *</label>
        <GroupSelector
          value={selectedGroup}
          onChange={handleGroupChange}
          authToken={userData.authToken}
          error={errors.groupId}
        />
        {errors.groupId && <p className="text-red-500 text-xs mt-1">{errors.groupId}</p>}
      </div>

      {/* Metadata */}
      <div>
        <label className="block text-sm font-medium mb-2">اطلاعات</label>
        <div className="space-y-2">
          {meta.map((m, i) => (
            <div key={i} className="flex gap-2">
              <input
                placeholder="عنوان"
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                value={m.key}
                onChange={(e) => updateMeta(i, "key", e.target.value)}
              />
              <input
                placeholder="مقدار"
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                value={m.value}
                onChange={(e) => updateMeta(i, "value", e.target.value)}
              />
              <button type="button" onClick={() => setMeta((p) => p.filter((_, j) => j !== i))}>
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="mt-2 text-sm text-blue-600 hover:underline"
          onClick={() => setMeta((p) => [...p, { key: "", value: "" }])}
        >
          + اضافه کردن
        </button>
      </div>

      {/* Files */}
      <div>
        <label className="block text-sm font-medium mb-2">فایلها *</label>
        <div className="grid grid-cols-4 gap-3 mb-3">
          {existingFiles
            .filter((f) => !removedFileIds.includes(f.id))
            .map((f) => (
              <div key={f.id} className="relative group">
                {isImage(f.type) ? (
                  <img
                    src={BASE_URL + "/" + f.url}
                    alt={f.name}
                    className="w-full h-20 object-cover rounded border"
                  />
                ) : (
                  <div className="w-full h-20 flex flex-col items-center justify-center bg-gray-100 rounded border">
                    <FileText size={24} className="text-gray-500" />
                    <span className="text-xs text-gray-500 mt-1 truncate w-full text-center px-1">
                      {f.name}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeExisting(f.id)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"
                >
                  <X size={12} />
                </button>
                <label>{f.title}</label>
              </div>
            ))}

          {newFiles.map((f, i) => (
            <div key={i} className="relative group">
              {f.file.type.startsWith("image") ? (
                <img
                  src={URL.createObjectURL(f.file)}
                  alt={f.file.name}
                  className="w-full h-20 object-cover rounded border"
                />
              ) : (
                <div className="w-full h-20 flex flex-col items-center justify-center bg-gray-100 rounded border">
                  <FileText size={24} className="text-gray-500" />
                  <span className="text-xs text-gray-500 mt-1 truncate w-full text-center px-1">
                    {f.file.name}
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={() => removeNew(i)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"
              >
                <X size={12} />
              </button>
                <label>{f.title}</label>
            </div>
          ))}
        </div>

        <div>
        <input
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
          placeholder="عنوان فایل"
          value={newFileTitle}
          onChange={(e) => setNewFileTitle( e.target.value )}
        />
        <input
          type="file"
          accept="image/*,.pdf"
          className="text-sm"
          title="انتخاب فایل"
          onChange={(e) => {
            if (e.target.files) setNewFiles([...newFiles, {file:e.target.files[0],title:newFileTitle}]);
            e.target.value = "";
          }}
        />
      </div>
        {errors.files && <p className="text-red-500 text-xs mt-1">{errors.files}</p>}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={loading}
          className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          {isEdit ? "ذخیره تغییرات" : "ثبت کردن"}
        </button>
        <button
          type="button"
          onClick={() => navigate("/admin/insurances")}
          className="px-5 py-2 rounded border border-gray-300 hover:bg-gray-50"
        >
          لغو
        </button>
      </div>
    </form>
  );
}
